import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { routerNavigationAction } from '@ngrx/router-store';

import { Store } from '@ngrx/store';
import { filter, map, switchMap } from 'rxjs';
import { EntitlementsActions } from '../actions/entitlements.actions';
import { concatLatestFrom } from '@ngrx/operators';
import {
  selectHasFeatures,
  selectHasPrivileges,
  selectHasRoles,
} from '../selectors/entitlements.selectors';
import { RolesService } from '@/app/services/roles.service';

@Injectable()
export class EntitlementsEffects {
  loadFeatures$;
  loadPrivileges$;
  loadRoles$;

  featuresLoaded$;
  privilegesLoaded$;
  rolesLoaded$;

  constructor(
    private actions$: Actions,
    store: Store,
    rolesService: RolesService,
  ) {
    this.loadFeatures$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(routerNavigationAction),
        concatLatestFrom(() => store.select(selectHasFeatures)),
        filter(
          ([action, hasFeatures]) =>
            !hasFeatures &&
            (action.payload.routerState.url.startsWith('/hq/users') ||
              action.payload.routerState.url.startsWith('/admin')),
        ),
        map(() => EntitlementsActions.loadFeatures()),
      );
    });

    this.loadPrivileges$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(routerNavigationAction),
        concatLatestFrom(() => store.select(selectHasPrivileges)),
        filter(
          ([action, hasPrivileges]) =>
            !hasPrivileges &&
            (action.payload.routerState.url.startsWith('/hq/users') ||
              action.payload.routerState.url.startsWith('/admin')),
        ),
        map(() => EntitlementsActions.loadPrivileges()),
      );
    });

    this.loadRoles$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(routerNavigationAction),
        concatLatestFrom(() => store.select(selectHasRoles)),
        filter(
          ([action, hasRoles]) =>
            !hasRoles &&
            (action.payload.routerState.url.startsWith('/hq/users') ||
              action.payload.routerState.url.startsWith('/admin')),
        ),
        map(() => EntitlementsActions.loadRoles()),
      );
    });

    this.featuresLoaded$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(EntitlementsActions.loadFeatures),
        switchMap(() => rolesService.loadFeatures()),
        map((features) => EntitlementsActions.featuresLoaded({ features })),
      );
    });

    this.privilegesLoaded$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(EntitlementsActions.loadPrivileges),
        switchMap(() => rolesService.loadPrivileges()),
        map((privileges) =>
          EntitlementsActions.privilegesLoaded({ privileges }),
        ),
      );
    });

    this.rolesLoaded$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(EntitlementsActions.loadRoles),
        switchMap(() => rolesService.loadRoles()),
        map((roles) => EntitlementsActions.rolesLoaded({ roles })),
      );
    });
  }
}
